import React, { useState } from 'react'
import { MqDesktop, MqTablet } from 'streamr-ui/css/consts'
import styled from 'styled-components'
import useHistoricalDataTokenStream from '~hooks/useHistoricalDataTokenStream'
import Layout from '~shared/Layout'
import PersonaPageBadge, {
    PersonaPageBadgeColumn,
    PersonaPageBadgeList,
} from '~shared/PersonaPage/PersonaPageBadge'
import PersonaPageFaq from '~shared/PersonaPage/PersonaPageFaq'
import PersonaPageHero from '~shared/PersonaPage/PersonaPageHero'
import PersonaPageSection from '~shared/PersonaPage/PersonaPageSection'
import binance from '~shared/PersonaPage/assets/binance.png'
import binance2x from '~shared/PersonaPage/assets/binance@2x.png'
import cmp from '~shared/PersonaPage/assets/cmp.png'
import cmp2x from '~shared/PersonaPage/assets/cmp@2x.png'
import PriceGraph from '~shared/PriceGraph'
import GraphNav from '~shared/PriceGraph/Nav'
import Spacer from '~shared/Spacer'
import Topic from '~shared/Topic'
import { Timeframe } from '~utils/consts'
import { DataTokenQAs } from '~utils/faqs'
import AddTokensAndChains from './AddTokensAndChains'
import Amounts from './Amounts'
import RolesAndEntities from './RolesAndEntities'
import Tokenomics from './Tokenomics'

const UnstyledDataToken = (props) => {
    const { today, week, month, year, all } = useHistoricalDataTokenStream()

    const [selectedInterval, setSelectedInterval] = useState(Timeframe.Day)

    const selectedData = () => {
        switch (selectedInterval) {
            case Timeframe.Week:
                return week
            case Timeframe.Month:
                return month
            case Timeframe.Year:
                return year
            case Timeframe.All:
                return all
            default:
                return today
        }
    }

    return (
        <Layout {...props}>
            <PersonaPageSection
                gradient="180deg, rgba(249, 220, 204, 0.57) 0%, rgba(245, 238, 255, 0.03) 100%"
                mobileGradient="180deg, rgba(249, 220, 204, 0.57) 1.78%, #f5f5f5 86.29%"
            >
                <PersonaPageHero
                    persona="hodler"
                    title="The DATA token"
                    desc={
                        <p>
                            The DATA token is an ERC-20 token used for project governance, to
                            incentivize the Network, to delegate stake on Streamr nodes, and for
                            Streamr&nbsp;Hub payments.
                        </p>
                    }
                />
                <Spacer pad head=",,2rem">
                    <PersonaPageBadgeColumn $desktopWidth="40rem">
                        <PersonaPageBadgeList singleColumn>
                            <PersonaPageBadge
                                iconImage={
                                    <img src={binance} srcSet={`${binance2x} 2x`} alt="Binance" />
                                }
                                href="https://www.binance.com?ref=IGUXHN0B"
                            >
                                Exchange on Binance
                            </PersonaPageBadge>
                        </PersonaPageBadgeList>
                    </PersonaPageBadgeColumn>
                </Spacer>
                <div id="price-chart" />
                <PriceGraphWrap>
                    <Spacer head="1rem,14.75rem,18.5rem" $appear>
                        <PriceGraphNav>
                            <h2>
                                <a href="#price-chart">DATA to USD chart</a>
                            </h2>
                            <div>
                                <GraphNav
                                    switchTimeframe={setSelectedInterval}
                                    loading={!today.length}
                                    $appear
                                />
                            </div>
                        </PriceGraphNav>
                    </Spacer>
                    <Spacer head="0rem,6.68rem,7.3rem">
                        <PriceGraph
                            points={selectedData()}
                            showTime={[Timeframe.Day, Timeframe.Week].includes(selectedInterval)}
                            theme={{
                                background: 'transparent',
                                area: '#F5F5F5',
                            }}
                        />
                    </Spacer>
                </PriceGraphWrap>
            </PersonaPageSection>
            <Topic pad head="0" backgroundColor="#F5F5F5">
                <Spacer pad head="6rem,," tail="6rem,,">
                    <AmountsWrap>
                        <Amounts />
                    </AmountsWrap>
                </Spacer>
                <AmountsBadgeColumn>
                    <PersonaPageBadgeList>
                        <PersonaPageBadge
                            href="https://coinmarketcap.com/currencies/streamr/"
                            iconImage={<img src={cmp} srcSet={`${cmp2x} 2x`} alt="CMP" />}
                        >
                            Streamr on CoinMarketCap
                        </PersonaPageBadge>
                    </PersonaPageBadgeList>
                </AmountsBadgeColumn>
                <Spacer pad head="6rem,,14rem" tail="6rem,10.75rem,14rem">
                    <AddDATA>
                        <AddTokensAndChains />
                    </AddDATA>
                </Spacer>
            </Topic>
            <div id="tokenomics" />
            <Tokenomics head="0" />
            <RolesAndEntities head="0" tail="0" />
            <PersonaPageFaq title={<>DATA tokens&nbsp;FAQ</>}>
                <DataTokenQAs />
            </PersonaPageFaq>
        </Layout>
    )
}

const AddDATA = styled.div`
    box-sizing: border-box;
    margin: 0 auto;
    max-width: 100%;
    padding: 0 1rem;
    width: 32rem;

    @media ${MqDesktop} {
        padding: 0;
        width: 51.5rem;
    }
`

const AmountsWrap = styled.div`
    box-sizing: border-box;
    max-width: 100%;
    padding: 0 2rem;
    margin: 0 auto;
    width: 20rem;

    @media ${MqTablet} {
        width: auto;
    }
`

const DataToken = styled(UnstyledDataToken)`
    color: #0c009a;
    padding-bottom: 9rem;

    @media ${MqTablet} {
        padding-bottom: 8.87rem;
    }

    @media ${MqDesktop} {
        padding-bottom: 10rem;
    }
`

const PriceGraphNav = styled.div`
    box-sizing: border-box;
    margin: 0 auto;
    max-width: 100%;
    padding: 0 2rem;

    h2 {
        flex-grow: 1;
        font-size: 2.5rem;
        line-height: normal;
        margin: 0;
    }

    h2 a {
        color: inherit !important;
        text-decoration: none !important;
    }

    > div {
        flex-shrink: 0;
        margin-top: 3.75rem;
        width: max-content;
    }

    @media (min-width: 56rem) {
        align-items: center;
        display: flex;
        width: 56rem;

        > div {
            margin-top: 0;
            width: auto;
        }
    }

    @media (min-width: 60rem) {
        width: 80.5rem;
        padding: 0 4rem;
    }
`

const PriceGraphWrap = styled.div`
    display: none;

    @media ${MqTablet} {
        display: block;
    }
`

const AmountsBadgeColumn = styled.div`
    box-sizing: border-box;
    max-width: 100%;
    padding: 0 1rem;
    margin: 0 auto;
    width: 32rem;
`

export default DataToken
